import type { Action, Subject } from 'shared/auth'

export const roleAbilities: Record<string, Array<[Action, Subject]>> = {
    'super-admin': [
        ['manage', 'all'],
        ['read', 'CompetitionMenu'],
        ['read', 'GeneralApplications'],
        ['update', 'GeneralApplicationsContacts'],
        ['read', 'RecreationMenu'],
        ['read', 'ManagementMenu'],
        ['read', 'CMSMenu'],
        ['read', 'UserProfile'],
        ['read', 'Competition'],
        ['read', 'Recreation'],
        ['manage', 'Recreation'],
        ['read', 'Organizations'],
        ['read', 'OrganizationUnits'],
        ['read', 'Seasons'],
        ['read', 'Sports'],
        ['read', 'Leagues'],
        ['read', 'Sponsors'],
        ['read', 'Users'],
        ['read', 'Accreditations'],
        ['read', 'RolesAndPermissions'],
        ['read', 'RecreationTypes'],
        ['read', 'Settings'],
        ['read', 'Pages'],
        ['read', 'Announcements'],
        ['read', 'Events'],
        ['read', 'Galleries'],
        ['read', 'Documents'],
        ['delete', 'Users'],
        ['read', 'ChangeStatus'],
        ['read', 'AddNewPermission'],
        ['manage', 'AddNewPermission'],
        ['read', 'UpdatePermission'],
        ['read', 'IssueAccreditation'],
        ['read', 'ChoseOrganization'],
        ['read', 'ChoseUnit'],
        ['read', 'UserInfo'],
    ],
    'organization-admin': [
        ['read', 'Organizations'],
        ['read', 'CompetitionMenu'],
        ['read', 'RecreationMenu'],
        ['read', 'GeneralApplications'],
        ['update', 'GeneralApplicationsContacts'],
        ['read', 'ManagementMenu'],
        ['read', 'UserProfile'],
        ['read', 'Competition'],
        ['manage', 'Competition'],
        ['manage', 'Phase'],
        ['read', 'Recreation'],
        ['read', 'OrganizationUnits'],
        ['manage', 'OrganizationUnits'],
        ['manage', 'AddNewPermission'],
        ['read', 'AddNewPermission'],
        ['read', 'Seasons'],
        ['read', 'Sponsors'],
        ['read', 'Users'],
        ['read', 'RolesAndPermissions'],
        ['read', 'Settings'],
        ['read', 'Pages'],
        ['read', 'Announcements'],
        ['read', 'Events'],
        ['read', 'Galleries'],
        ['read', 'Documents'],
        ['read', 'ChoseOrganization'],
        ['read', 'ChoseUnit'],
        ['read', 'Leagues'],
        ['manage', 'Leagues'],
    ],
    'technical-delegate': [
        ['read', 'UserProfile'],
        ['read', 'Competition'],
        ['read', 'Phase'],
        ['create', 'Phase'],
        ['update', 'Phase'],
        ['read', 'CompetitionMenu'],
        ['read', 'RecreationMenu'],
        ['read', 'Recreation'],
        ['read', 'Users'],
        ['read', 'RolesAndPermissions'],
        ['read', 'ChoseOrganization'],
        ['read', 'ChoseUnit'],
    ],
    technician: [
        ['read', 'UserProfile'],
        ['read', 'RecreationMenu'],
        ['read', 'Recreation'],
        ['read', 'Users'],
        ['read', 'UserInfo'],
        ['read', 'ManagementMenu'],
        ['read', 'ChangeStatus'],
        ['read', 'Accreditations'],
        ['read', 'IssueAccreditation'],
        ['manage', 'Accreditations'],
        ['read', 'ManagementMenu'],
    ],
    notetaker: [
        ['read', 'UserProfile'],
        ['read', 'RecreationMenu'],
        ['read', 'Recreation'],
    ],
    'unit-admin': [
        ['read', 'CompetitionMenu'],
        ['read', 'RecreationMenu'],
        ['read', 'GeneralApplications'],
        ['update', 'GeneralApplicationsContacts'],
        ['read', 'ManagementMenu'],
        ['read', 'UserProfile'],
        ['read', 'Competition'],
        ['read', 'Recreation'],
        ['read', 'Users'],
        ['read', 'RolesAndPermissions'],
        ['read', 'CompetitionDocumentDetails'],
        ['read', 'ChoseUnit'],
        ['read', 'AddNewPermission'],
        ['read', 'OrganizationUnits'],
        ['manage', 'OrganizationUnits'],
    ],
    'team-admin': [
        ['read', 'UserProfile'],
        ['read', 'Competition'],
        ['read', 'CompetitionMenu'],
        ['read', 'RecreationMenu'],
        ['read', 'GeneralApplications'],
        ['read', 'ChoseUnit'],
        ['read', 'Recreation'],
        ['read', 'Users'],
        ['read', 'RolesAndPermissions'],
        ['read', 'CompetitionDocumentDetails'],
    ],
    'recreation-admin': [
        ['read', 'UserProfile'],
        ['read', 'RecreationMenu'],
        ['read', 'Recreation'],
        ['manage', 'Recreation'],
        ['manage', 'CreateRecreation'],
        ['read', 'RolesAndPermissions'],
        ['read', 'RecreationTypes'],
        ['read', 'AddNewPermission'],
    ],
    'recreation-trainer': [
        ['read', 'RecreationMenu'],
        ['read', 'UserProfile'],
        ['read', 'Recreation'],
        ['manage', 'Recreation'],
    ],
    'cms-admin': [
        ['read', 'UserProfile'],
        ['read', 'CMSMenu'],
        ['manage', 'CmsPublications'],
        ['manage', 'CmsEvents'],
        ['read', 'CmsPages'],
        // Admins build new website pages themselves (e.g. competition pages)
        // and their English translations; archiving stays super-admin only.
        ['create', 'CmsPages'],
        ['update', 'CmsPages'],
        ['manage', 'CmsDocuments'],
        ['manage', 'CmsSponsors'],
        ['manage', 'CmsMembers'],
        ['manage', 'CmsInstitutions'],
        ['manage', 'CmsCompetitionTypes'],
        ['manage', 'CmsSportsImages'],
        ['manage', 'CmsSettings'],
        ['read', 'Settings'],
        ['read', 'Pages'],
        ['read', 'Announcements'],
        ['read', 'Events'],
        ['read', 'Documents'],
        ['read', 'RecreationMenu'],
        ['read', 'Recreation'],
    ],
    'cms-editor': [
        ['read', 'UserProfile'],
        ['read', 'CMSMenu'],
        ['read', 'CmsPublications'],
        ['create', 'CmsPublications'],
        ['update', 'CmsPublications'],
        ['read', 'CmsEvents'],
        ['create', 'CmsEvents'],
        ['update', 'CmsEvents'],
        ['read', 'CmsPages'],
        ['update', 'CmsPages'],
        ['read', 'CmsDocuments'],
        ['create', 'CmsDocuments'],
        ['update', 'CmsDocuments'],
        ['read', 'CmsMembers'],
        ['create', 'CmsMembers'],
        ['update', 'CmsMembers'],
        ['read', 'CmsInstitutions'],
        ['create', 'CmsInstitutions'],
        ['update', 'CmsInstitutions'],
        ['read', 'CmsCompetitionTypes'],
        ['create', 'CmsCompetitionTypes'],
        ['update', 'CmsCompetitionTypes'],
        ['read', 'CmsSportsImages'],
        ['update', 'CmsSportsImages'],
        ['read', 'Settings'],
        ['read', 'Pages'],
        ['read', 'Announcements'],
        ['read', 'Events'],
        ['read', 'Documents'],
        ['read', 'RecreationMenu'],
        ['read', 'Recreation'],
    ],
    user: [
        ['read', 'UserProfile'],
        ['read', 'Recreation'],
        ['read', 'RecreationMenu'],
    ],
}

export const ROLE_HIERARCHY = {
    'super-admin': ['*'],
    'organization-admin': [
        'unit-admin',
        'team-admin',
        'technical-delegate',
        'notetaker',
        'recreation-admin',
        'recreation-trainer',
        'cms-admin',
        'cms-editor',
        'organization-admin',
    ],
    'unit-admin': ['team-admin', 'unit-admin'],
    'cms-admin': ['cms-editor'],
    'cms-editor': [],
    'team-admin': ['technical-delegate', 'notetaker'],
    'recreation-admin': ['recreation-trainer'],
    'recreation-trainer': [],
    'technical-delegate': ['notetaker'],
    technician: [],
} as const

export const ROLE_ABILITY_MAP = {
    'super-admin': { action: 'manage', subject: 'all' },
    'organization-admin': { action: 'read', subject: 'ManagementMenu' },
    'unit-admin': { action: 'read', subject: 'ManagementMenu' },
    'team-admin': { action: 'read', subject: 'Competition' },
    'cms-admin': { action: 'read', subject: 'CMSMenu' },
    'recreation-admin': { action: 'read', subject: 'RecreationMenu' },
    'technical-delegate': {
        action: 'read',
        subject: 'Competition',
    },
    technician: { action: 'read', subject: 'Users' },
} as const

export const ROLE_NAME_TO_KEY = {
    'Super Admin': 'super-admin',
    'Admin Organizacije': 'organization-admin',
    'Admin Sastavnice': 'unit-admin',
    'Voditelj Ekipe': 'team-admin',
    'Tehnički Delegat': 'technical-delegate',
    Zapisničar: 'notetaker',
    'Admin Rekreacija': 'recreation-admin',
    'Trener Rekreacije': 'recreation-trainer',
    'CMS Admin': 'cms-admin',
    'CMS Editor': 'cms-editor',
    Tehničar: 'technician',
} as const
